import React from 'react';
import { usePricingFigures, usd } from '../../../hooks/usePricingFigures';
const head = 'text-[11.5px] font-bold uppercase tracking-[0.12em] text-[#c5cdd3] whitespace-nowrap';
const note = 'mt-2 text-[12.5px] leading-snug text-[#aab4bc]';
interface Fee {
  label: string;
  value: string;
  unit?: string;
  sub: string;
  /** Promo deadline, emphasized under the note. */
  deadline?: string | null;
  accent?: boolean;
  /** Matched bonus rows replacing the single figure (Flagship's day-45 bonus). */
  bonusRows?: {
    label: string;
    value: string;
  }[] | null;
}

/**
 * Four figures side by side, projected hires largest. Every label and figure
 * sits on the same top line, so notes of different lengths never shift the
 * figures out of alignment.
 */
export function PricingFigureRow({
  includeLeader
}: {
  includeLeader: boolean;
}) {
  const f = usePricingFigures(includeLeader);
  const hasRetention = f.retentionBonus > 0;
  const fees: Fee[] = [...(f.showImplementation ? [{
    label: 'Implementation',
    value: usd(f.implementation),
    sub: f.implementationNote,
    deadline: f.promoDeadlineText
  }] : []), {
    label: 'Monthly service',
    value: usd(f.monthly),
    unit: '/ mo',
    sub: f.monthlyNote
  }, hasRetention ? {
    label: 'Performance bonuses',
    value: '',
    bonusRows: [{
      label: 'Placement · at day 15',
      value: usd(f.bonus)
    }, {
      label: 'Retention · at day 45',
      value: usd(f.retentionBonus)
    }],
    sub: 'per rep still active in the field',
    accent: true
  } : {
    label: 'Placement bonus',
    value: usd(f.bonus),
    unit: '/ rep',
    sub: f.bonusNote,
    accent: true
  }];
  // The bonus column gets a little more room so "$1,000 / rep" never wraps.
  const columns = fees.map(fee => fee.bonusRows ? '1.15fr' : 'minmax(0, 1fr)').join(' ');
  return <div data-id="element-851" data-mp="1fhrzkvrbk0g1">
      <div className="grid items-start gap-6" style={{
      gridTemplateColumns: `${columns} 1.3fr`
    }} data-id="element-852" data-mp="1fggumsm50mnc">
        {fees.map(fee => <div key={fee.label} data-id="element-853" data-mp="1be29u2ywrctf">
            <div className={head} data-id="element-854" data-mp="16g0vfvcfg1oa">{fee.label}</div>
            {fee.bonusRows ? <div className="mt-2 space-y-2.5" data-id="element-855" data-mp="11hz9n2nm9o94">
                {fee.bonusRows.map(row => <div key={row.label} data-id="element-856" data-mp="wie2w627gblu">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#aab4bc] whitespace-nowrap" data-id="element-857" data-mp="wjflqwpgq2e4">
                      {row.label}
                    </div>
                    <div className="brief-display mt-1 text-[28px] font-extrabold leading-none whitespace-nowrap text-[#6fd6b0]" data-id="element-858" data-mp="nrdplamff89n">
                      {row.value}
                      <span className="ml-1 text-[14px] font-bold" data-id="element-859" data-mp="drv9545q64mc">/ rep</span>
                    </div>
                  </div>)}
              </div> : <div className={`brief-display mt-2 text-[38px] font-extrabold leading-none whitespace-nowrap ${fee.accent ? 'text-[#6fd6b0]' : 'text-white'}`} data-id="element-860" data-mp="8ve9pdt2s3ty">
                {fee.value}
                {fee.unit && <span className={`ml-1 text-[16px] font-bold ${fee.accent ? 'text-[#6fd6b0]' : 'text-[#c5cdd3]'}`} data-id="element-861" data-mp="8l7yb0htbbh8">
                    {fee.unit}
                  </span>}
              </div>}
            <div className={note} data-id="element-862" data-mp="3h0zylarxd4zk">{fee.sub}</div>
            {fee.deadline && <div className="mt-1 text-[14px] font-bold leading-snug text-[#6fd6b0] whitespace-nowrap" data-id="element-863" data-mp="3m0w63jztzejv">
                {fee.deadline}
              </div>}
          </div>)}
        <div className="self-stretch border-l border-[#33404a] pl-6" data-id="element-864" data-mp="2td0ayjpwtup7">
          <div className={head} data-id="element-865" data-mp="2ybtizwmfgthz">Projected hires / mo</div>
          <div className="brief-display mt-2 text-[56px] font-extrabold leading-[0.85] text-[#6fd6b0] whitespace-nowrap" data-id="element-866" data-mp="2yaa1k2p1ccem">
            {f.hiresLow}–{f.hiresHigh}
          </div>
          <div className={note} data-id="element-867" data-mp="yz4lwhw86k8h">
            {usd(f.costLow)}–{usd(f.costHigh)} all-in per hire, incl. est. bonuses
          </div>
        </div>
      </div>
      <p className="mt-4 pt-3 border-t border-[#33404a] text-[13.5px] text-[#aab4bc]" data-id="element-868" data-mp="18v7m3i7io8ss">
        Recommended ad spend{' '}
        <span className="font-bold text-white" data-id="element-869" data-mp="1mlkshg4rffg0">{usd(f.adSpend)} / mo</span>, {f.adsNote},
        not to D2D HIRE.
      </p>
    </div>;
}